import React from 'react';
import { ProTable, ProColumns, ModalForm, ProFormText, ProFormTextArea } from '@ant-design/pro-components';
import { Button, Space, Popconfirm, Tag, Typography, Card, Input } from 'antd';
import { PlusOutlined, DeleteOutlined, TagOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';
import { useTopics, useCreateTopic, useDeleteTopic } from '../api';
import { Topic, CreateTopicRequest } from '../types';
import { addTopicModalVisibleAtom } from '../atoms';

const { Text } = Typography;

const TopicsTab: React.FC = () => {
    const [addModalVisible, setAddModalVisible] = useAtom(addTopicModalVisibleAtom);

    const { data: topicsData, isLoading } = useTopics();
    const createTopicMutation = useCreateTopic();
    const deleteTopicMutation = useDeleteTopic();

    const topics = topicsData?.data || [];

    const handleCreate = async (values: CreateTopicRequest & { colorValue?: string }) => {
        try {
            const payload: CreateTopicRequest = {
                name: values.name,
                description: values.description,
                color: values.colorValue || '#1890ff',
            };
            await createTopicMutation.mutateAsync(payload);
            setAddModalVisible(false);
            return true;
        } catch (error) {
            return false;
        }
    };

    const handleDelete = async (id: string) => {
        await deleteTopicMutation.mutateAsync(id);
    };

    const columns: ProColumns<Topic>[] = [
        {
            title: 'Color',
            dataIndex: 'color',
            key: 'color',
            width: 80,
            render: (color: string) => (
                <div
                    style={{
                        width: 24,
                        height: 24,
                        backgroundColor: color,
                        borderRadius: 4,
                        border: '1px solid #d9d9d9',
                    }}
                />
            ),
            search: false,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name: string, record: Topic) => (
                <Tag color={record.color} style={{ color: '#fff', fontSize: 14, padding: '4px 8px' }}>
                    <TagOutlined style={{ marginRight: 4 }} />
                    {name}
                </Tag>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (description: string) => (
                description ? (
                    <Text ellipsis={{ tooltip: description }} style={{ maxWidth: 300 }}>
                        {description}
                    </Text>
                ) : (
                    <Text type="secondary">No description</Text>
                )
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
            sorter: true,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record: Topic) => (
                <Space>
                    <Popconfirm
                        title="Delete Topic"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleteTopicMutation.isLoading}
                            title="Delete topic"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card style={{ marginBottom: 16 }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>
                        Manage topic categories for organizing and categorizing Instagram content.
                    </Text>
                    <Text type="secondary">
                        Total topics: {topics.length}
                    </Text>
                </Space>
            </Card>

            <ProTable<Topic>
                columns={columns}
                dataSource={topics}
                rowKey="id"
                loading={isLoading}
                search={{
                    labelWidth: 'auto',
                }}
                toolbar={{
                    actions: [
                        <Button
                            key="add"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setAddModalVisible(true)}
                        >
                            Add Topic
                        </Button>,
                    ],
                }}
                pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50'],
                }}
            />

            <ModalForm<CreateTopicRequest & { colorValue?: string }>
                title="Add Topic"
                open={addModalVisible}
                onOpenChange={setAddModalVisible}
                onFinish={handleCreate}
                modalProps={{
                    destroyOnClose: true,
                }}
                submitter={{
                    submitButtonProps: {
                        loading: createTopicMutation.isLoading,
                    },
                }}
                layout="horizontal"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                initialValues={{
                    colorValue: '#1890ff',
                }}
            >
                <ProFormText
                    name="name"
                    label="Topic Name"
                    placeholder="Enter topic name"
                    rules={[
                        { required: true, message: 'Please enter topic name' },
                        { max: 50, message: 'Topic name must be less than 50 characters' },
                    ]}
                />

                <ProFormTextArea
                    name="description"
                    label="Description"
                    placeholder="Enter topic description (optional)"
                    fieldProps={{
                        rows: 3,
                        maxLength: 200,
                        showCount: true,
                    }}
                />

                <ProFormText
                    name="colorValue"
                    label="Color"
                    rules={[
                        { required: true, message: 'Please enter a color' },
                        { pattern: /^#[0-9A-Fa-f]{6}$/, message: 'Please enter a valid hex color (e.g., #1890ff)' },
                    ]}
                    fieldProps={{
                        placeholder: '#1890ff',
                        addonBefore: (
                            <div
                                style={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: '#1890ff',
                                    borderRadius: 4,
                                    border: '1px solid #d9d9d9',
                                }}
                            />
                        ),
                    }}
                    tooltip="Enter a hex color code (e.g., #1890ff). Popular colors: #1890ff (blue), #52c41a (green), #faad14 (orange), #f5222d (red), #722ed1 (purple)"
                />
            </ModalForm>
        </div>
    );
};

export default TopicsTab;